'use client';
import Container from '../container';
import { useTranslations } from 'next-intl';
import { pages } from '@/constants/pages';
import NextLink from '@/components/next-link';
import { usePathname } from '@/i18n/navigation';
import BreadCrumbItem from './bread-crumb-item';
import useCategoryStore from '@/store/category.store';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { findObjectById, modifyMenuTreeData } from '@/utils/global';
import SortAndResult from '@/components/category/filters/sort-and-result';

const Breadcrumb = ({
  current,
  isSearchPage,
  productCategory,
  categoryId,
  handleSorting,
}) => {
  const t = useTranslations('pages');
  const pathname = usePathname()?.slice(1);
  const slugs = useCategoryStore((s) => s?.slugs);
  const isFixedBreadcrumb = pathname.includes('user/orders') || pages[pathname];

  const { categoryTreeData } = useCategoryStore((state) => state);
  const categories = useMemo(() => {
    return categoryId && categoryTreeData?.children_data
      ? modifyMenuTreeData(categoryTreeData.children_data)
      : null;
  }, [categoryId, categoryTreeData?.children_data]);

  const [breadcrumbItems, setBreadcrumbItems] = useState([]);

  const buildBreadcrumbItems = useCallback((pathSegments, categories) => {
    if (!Array.isArray(pathSegments) || !Array.isArray(categories)) return [];
    return pathSegments
      .map((segment) => {
        if (!segment) return null;

        const matched = findObjectById(categories, 'innova_url', segment);
        return matched
          ? { name: matched.name, link: matched.currentURL, segment }
          : null;
      })
      .filter(Boolean);
  }, []);

  useEffect(() => {
    if (!categoryId || !Array.isArray(categories) || !categories.length) {
      setBreadcrumbItems([]);
      return;
    }
    const pathSegments = pathname.split('/').filter(Boolean);
    const newItems = buildBreadcrumbItems(pathSegments, categories);

    setBreadcrumbItems((prev) => {
      if (prev.length !== newItems.length) return newItems;
      const hasChanged = prev.some(
        (item, index) =>
          item.id !== newItems[index]?.id ||
          item.name !== newItems[index]?.name ||
          item.link !== newItems[index]?.link
      );

      return hasChanged ? newItems : prev;
    });
  }, [categoryId, pathname, categories, buildBreadcrumbItems]);

  const categoriesForProduct = useMemo(() => {
    if (!productCategory || !Array.isArray(productCategory)) return [];
    const category = productCategory[1];
    if (!category) return [];
    return [
      {
        name: category.name,
        url: `c/${slugs[category.category_id]}`,
      },
    ];
  }, [productCategory, slugs]);

  return (
    <div className='bg-tertiary-100 flex min-h-[30px] items-center sm:min-h-[35px] lg:min-h-[46px]'>
      <Container
        className={`flex items-center justify-between !py-0 ${isSearchPage && '!py-0.75'}`}
      >
        <div className='flex items-center overflow-x-hidden overflow-y-hidden'>
          <NextLink
            className='hover:text-primary whitespace-nowrap max-md:text-sm max-sm:text-xs'
            href='/'
          >
            {t('home')}
          </NextLink>

          {breadcrumbItems.map((cat, idx) => (
            <BreadCrumbItem
              key={`category-${cat.id}`}
              item={cat}
              href={`/c${cat.link}`}
              isLast={idx === breadcrumbItems.length - 1}
            />
          ))}

          {categoriesForProduct?.map((cat, idx) => (
            <BreadCrumbItem
              key={`product-category-${idx}`}
              item={cat}
              href={`/${cat.url}`}
            />
          ))}

          {current && !categoryId && (
            <BreadCrumbItem item={{ name: current }} isLast={true} />
          )}

          {isFixedBreadcrumb && (
            <>
              &nbsp;&nbsp;&gt;&nbsp;
              <span className='font-bold max-md:text-sm max-sm:text-xs'>
                {t(
                  pathname.includes('user/orders')
                    ? 'My Orders'
                    : pages[pathname]
                )}
              </span>
            </>
          )}
        </div>

        {isSearchPage && (
          <div className='max-sm:hidden'>
            <SortAndResult isSearchPage onSort={handleSorting} />
          </div>
        )}
      </Container>
    </div>
  );
};

export default Breadcrumb;
